import React from 'react';
import PropTypes from 'prop-types';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import dayjs from '../../../dayjs/dayjs_timezone';
import Alert from '../../components/Alert';
import CloseModalIcon from '../components/CloseModalIcon';
import ConfirmationPage from '../components/ApptConfirmationPage';
import CreatedBookingPage from '../components/CreatedBookingPage';
import DatePicker from '../../components/DatePicker';
import HelpTooltip from '../components/HelpTooltip';
import MultipleAppointments from '../components/MultipleAppointments';
import Price from '../components/Price';
import PaymentOptions from '../components/PaymentOptions';
import ProductSelect from '../components/selects/ProductSelect';
import ProductWidgetPage from '../ProductWidgetPage';
import TimeSlotsSelect from '../components/TimeSlot/TimeSlotsSelect';
import TooltipDetails from '../components/TooltipDetails';
import UpdatedBookingPage from '../components/UpdatedBookingPage';
import { FULL_DATE_TIME_FORMAT, SHORT_DATE_FORMAT } from '../../constants';
import Translation from '../../utilities/localizationUtility';
import {
  detectIOS,
  detectMob,
  enumerateDaysBetweenDates,
  getMaxTimeslotsAvailability,
  getTooltipParams,
  isHiddenVariant,
  parseAvailableDates,
} from '../../utilities/helperUtility';
import { stringToBoolean } from '../../utilities/conversionUtility';
import { parseBookingResponseError } from '../../utilities/parser';
import { dateFromParams } from '../../utilities/timeUtility';
import { StyledTimeZone, StyledGlobalWrapper } from './AppointmentPage.style';
import BrandingString from '../components/BrandingString';

const fetchBlockAttributes = (order) => ({
  selectedLocation: order.selectedLocation,
  selectedResource: order.selectedResource,
  variantSelected: order.variantSelected,
});

export default class AppointmentPage extends ProductWidgetPage {
  constructor(props) {
    super(props);

    this.state = {
      availableDates: [],
      bookingCreated: false,
      currentMonth: null,
      daySelected: null,
      hoverStart: null,
      hoverEnteredTo: null,
      isModalOpen: false,
      messages: [],
      modalHeight: 100,
      preventFetchBlocks: false,
      spaceOnDays: {},
      timeslotLoading: false,
      tooltipParams: null,
    };

    this.onDayClick = this.onDayClick.bind(this);
    this.handleCloseHelpTooltip = this.handleCloseHelpTooltip.bind(this);
    this.handleDayMouseEnter = this.handleDayMouseEnter.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.onSelectTimeSlot = this.onSelectTimeSlot.bind(this);
    this.onMonthClick = this.onMonthClick.bind(this);
  }

  componentDidMount() {
    const {
      exceededBookingsLimit,
      exceededTrialPlan,
      minDate,
      timeslots,
      timeZone,
      orderActions: { setTimeSlot },
      urlParams,
    } = this.props;
    const date = dateFromParams(urlParams, timeZone);
    super.componentDidMount();
    this.setState({ availableDates: parseAvailableDates(timeslots) });

    if (!exceededTrialPlan && !exceededBookingsLimit) {
      if (date) {
        const day = date.format(SHORT_DATE_FORMAT);
        this.onMonthClick(date)?.then((data) => {
          const { blocks } = data;
          let newStates = { currentMonth: date };
          const block = blocks.find((x) => x.date === day);
          const slot = block ? block.timeslots.find((x) => dayjs(x.start).isSame(date)) : null;
          if (slot) {
            newStates = { ...newStates, daySelected: date.toDate(), timeSlotSelected: slot };
            setTimeSlot({ timeSlot: slot });
          }
          this.setState(newStates);
        });
      } else {
        this.setState({ preventFetchBlocks: true });
        this.defaultFetchBlocks()?.then((data) => {
          const { blocks } = data;
          const today = dayjs().tz(timeZone);
          const firstDate = minDate ? today.clone().add(minDate, 'seconds') : today.clone();
          this.getFirstAvailabileBlock(blocks, firstDate);
        });
      }
    }
  }

  componentDidUpdate(oldProps) {
    const { daySelected, currentMonth, preventFetchBlocks, timeSlotSelected } = this.state;
    const {
      timeslots,
      minDate,
      timeZone,
      variants,
      order: nextOrder,
      frameActions: { fetchBlocks, setBlocks },
      orderActions: { removeTimeSlots, setOrderDetails, setTimeSlot },
      setting,
      exceededBookingsLimit,
      exceededTrialPlan,
    } = this.props;
    const { order: prevOrder, setting: prevSetting, timeZone: prevTimeZone, timeslots: prevTimeslots } = oldProps;
    const isIntervalBased = setting.time_slot_generation === 'interval';
    const interval = setting.slot_interval ? setting.slot_interval : 60;
    const intervalValue = isIntervalBased ? interval : null;
    let date = '';

    if (currentMonth) {
      date = currentMonth;
    } else {
      date = dayjs();

      if (minDate) {
        date = date.clone().add(minDate, 'seconds');
      }

      this.setState({ currentMonth: date });
    }

    const start = date.clone().startOf('month').startOf('week').subtract(1, 'days');
    const finish = date.clone().endOf('month').endOf('week').add(1, 'days');
    const availableDatesTemp = nextOrder.variantSelected
      ? parseAvailableDates(timeslots, { force: this.showWaitlist() })
      : [];

    if (!isEqual(timeslots, prevTimeslots)) {
      this.setState({ availableDates: availableDatesTemp });
    }

    if (
      !isEqual(timeslots, prevTimeslots)
      || !isEqual(nextOrder.quantity, prevOrder.quantity)
      || !isEqual(nextOrder.totalQuantity, prevOrder.totalQuantity)
    ) {
      let selectedTimeslot;
      const nextQuantity = nextOrder.quantity;

      if (timeSlotSelected?.available < nextQuantity) {
        date = dayjs(timeSlotSelected.start).format(SHORT_DATE_FORMAT);
        const blockTimeSlot = find(timeslots, { date });
        if (blockTimeSlot) {
          const blockTimeSlots = blockTimeSlot.timeslots;
          let firstAvailableSlot = blockTimeSlots.filter(
            (slot) => slot.status === 1 && slot.available >= nextQuantity,
          )[0];

          if (!firstAvailableSlot) {
            firstAvailableSlot = blockTimeSlots.filter((slot) => slot.status === 1)[0] || blockTimeSlot.timeslots[0];
          }
          selectedTimeslot = firstAvailableSlot;
        }
      }

      const spaces = {};
      timeslots.forEach((slot) => {
        spaces[slot.date] = this.calculateSpaceOnDay(slot.date);
      });

      this.setState({ spaceOnDays: spaces, timeSlotSelected: selectedTimeslot || timeSlotSelected });
    }

    if ((!nextOrder.variantSelected && timeSlotSelected)
      || (daySelected && !availableDatesTemp.find((avDate) => dayjs(avDate).isSame(dayjs(daySelected), 'day')))
    ) {
      setTimeSlot({ timeSlot: null });
      this.setState({ daySelected: null, timeSlotSelected: null });
    }

    if (!isEqual(nextOrder.variantSelected, prevOrder.variantSelected)) {
      const { variantSelected, quantity } = nextOrder;
      const selectedVariant = variantSelected ? variants.find((v) => v.external_id === variantSelected) : {};
      const multiplier = selectedVariant ? selectedVariant.unit_multiplier : 1;

      setOrderDetails({ totalQuantity: quantity * multiplier });
    }

    // TODO variantSelected should refactor to object, its ID now
    if (
      nextOrder.variantSelected
      && (!isEqual(fetchBlockAttributes(nextOrder), fetchBlockAttributes(prevOrder))
        || (!isEqual(prevSetting, setting))
      )
      // The interval cannot be less than 5 minutes
      && (!isIntervalBased || interval >= 5)
      && !exceededTrialPlan
      && !exceededBookingsLimit
      && !preventFetchBlocks
      && !isHiddenVariant(variants, nextOrder.variantSelected)
    ) {
      if (this.props.multipleAppointmentsEnabled && prevOrder.multipleSlotsCount > 1) {
        removeTimeSlots();
        this.setState({ daySelected: null, timeSlotSelected: null });
      }
      fetchBlocks({
        externalId: nextOrder.variantSelected,
        interval: intervalValue,
        finish,
        locationId: nextOrder.selectedLocation ? nextOrder.selectedLocation.id : [],
        resourceId: nextOrder.selectedResource ? nextOrder.selectedResource.id : [],
        start,
        timezone: timeZone,
      });
    }

    if (!isEqual(prevTimeZone, timeZone)) {
      setBlocks(timeslots, timeZone);
    }
  }

  calculateSpaceOnDay(day) {
    const { timeslots, order: { totalQuantity = 1 }, timeZone } = this.props;
    const dayFormat = dayjs.tz(day, timeZone).format(SHORT_DATE_FORMAT);
    const blockTimeSlot = find(timeslots, { date: dayFormat });

    if (blockTimeSlot) {
      return blockTimeSlot.timeslots.filter((x) => x.available >= totalQuantity).length;
    }

    return 0;
  }

  defaultFetchBlocks() {
    const {
      exceededBookingsLimit,
      exceededTrialPlan,
      minDate,
      order,
      resources,
      setting,
      timeZone,
      frameActions: { fetchBlocks },
      orderActions: { setSelectedResource },
      variants,
    } = this.props;
    const isIntervalBased = setting.time_slot_generation === 'interval';
    const interval = setting.slot_interval ? setting.slot_interval : 60;
    const intervalValue = isIntervalBased ? interval : null;

    if (!order.selectedResource && resources.length === 1) {
      setSelectedResource(resources[0]);
    }
    if (exceededTrialPlan || exceededBookingsLimit) {
      return null; // do not sent default request when exceeded trial plan
    }
    if (!order.variantSelected) {
      return null; // do not sent default request when no variant
    }
    if (isIntervalBased && interval < 5) {
      return null; // The interval cannot be less than 5 minutes
    }

    let date = dayjs();

    if (minDate) {
      date = date.clone().add(minDate, 'seconds');
    }

    const start = date.clone().startOf('month').startOf('week').subtract(1, 'days');
    const finish = date.clone().endOf('month').endOf('week').add(1, 'days');
    const locationId = order.selectedLocation?.id;
    const resourceId = order.selectedResource?.id || (resources.length === 1 ? resources[0].id : null);

    return fetchBlocks({
      externalId: order.variantSelected,
      finish,
      interval: intervalValue,
      locationId: locationId ? [locationId] : null,
      resourceId,
      start,
      timezone: timeZone,
    });
  }

  enabledBookingCreation() {
    const { setting: { cart_bypass, mode }, order: { totalPrice = 0 } } = this.props;
    return totalPrice === 0 && (stringToBoolean(cart_bypass) || mode === 'button');
  }

  getButtonDetails() {
    const { setting: { mode }, style: { body = {}, button = {} } } = this.props;

    if (mode !== 'button') {
      return super.getButtonDetails();
    }
    const t = new Translation(this.props);

    return {
      label: t.key('submit'),
      styles: `
        .bta-widget-show-button:hover {
          background-image: initial !important;
          box-shadow: 0px 0px 6px 1px rgb(0 0 0 / 75%);
          color: ${button.hover.color};
        }
        .bta-widget-show-button {
          color: ${button.color};
          background-color: ${button.backgroundColor};
          border: 1px solid transparent;
          border-radius: 2px;
          font-family: ${body.fontFamily};
          font-size: calc(16px + 4px);
          font-weight: 700;
          line-height: normal;
          min-width: 110px;
          padding: 9px;
          white-space: pre-wrap;
        }
      `,
    };
  }

  getFirstAvailabileBlock(blocks, date) {
    const { maxDate, timeZone } = this.props;
    const maxDateObj = maxDate ? dayjs().tz(timeZone).add((maxDate), 'days') : null;
    const firstAvailableBlock = blocks.find((block) => {
      if(this.showWaitlist()) return true

      return block.available_slot_count > 0
    });
    const availableDate = firstAvailableBlock ? firstAvailableBlock.date : date.add(1, 'month');
    const availableDateObj = dayjs(availableDate).tz(timeZone);
    const availableMonth = availableDateObj.month();

    if (availableMonth > date.month() && (!maxDate || (availableMonth <= maxDateObj.month()))) {
      this.onMonthClick(availableDate)?.then((data) => {
        this.getFirstAvailabileBlock(data.blocks, availableDateObj);
      });
    } else {
      this.setState({ preventFetchBlocks: false });
    }
  }

  getMaxQuantity() {
    const { timeSlotSelected } = this.state;
    const { timeslots } = this.props;
    return timeSlotSelected ? timeSlotSelected.available : getMaxTimeslotsAvailability(timeslots);
  }

  getNextButtonLabel(canBook) {
    const { timeSlotSelected } = this.state;
    const { order: { variantSelected } } = this.props;
    const t = new Translation(this.props);
    let label = canBook ? t.key('nextButton') : t.key('noAvailability');

    if (variantSelected && !timeSlotSelected) {
      label = t.key('makeSelection');
    }

    return label;
  }

  getSpaceOnDay(day) {
    const { spaceOnDays } = this.state;
    const { timeZone } = this.props;
    const dayFormat = dayjs.tz(day, timeZone).format(SHORT_DATE_FORMAT);
    const spaces = spaceOnDays[dayFormat];

    return spaces >= 0 ? spaces : this.calculateSpaceOnDay(day);
  }

  getSubmitButtonLabel() {
    const { timeSlotSelected } = this.state;
    const { bookingToken, order: { variantSelected } } = this.props;
    const t = new Translation(this.props);
    let submitLabel;

    if (!timeSlotSelected) {
      submitLabel = t.key('makeSelection');
    } else if (bookingToken) {
      submitLabel = t.key('reschedule') || 'Reschedule';
    }

    if (!variantSelected) {
      submitLabel = t.key('noAvailability');
    }

    return submitLabel;
  }

  getValidMessage() {
    const messages = [];

    // https://trello.com/c/BrR72bJb
    //  this.checkValidResources(messages);

    this.checkValidLocation(messages);

    return messages;
  }

  handleAddItems(reservation, callback) {
    const {
      order: { quantity, selectedResource, timeSlots },
      resources,
    } = this.props;

    let sliceIndex = timeSlots.length || 1;
    if (resources.length > 1 && quantity > 1 && !selectedResource?.id) sliceIndex *= quantity;

    const type = ['Date', 'Time', 'Location'];
    const visibleResource = this.getEnableResourceDropdown();

    this.onAddItems({
      reservation,
      firstSliceParam: -sliceIndex,
      secondSliceParam: reservation.items?.length ?? 0,
      type,
      addItemsActionParam: { visibleResource },
      callback,
    });
  }

  handleCloseHelpTooltip() {
    this.setState({ tooltipParams: null });
  }

  handleGetValidationFn(type, validationFn) {
    if (type === 'additional') {
      this.validateAdditionalDetails = validationFn;
    }
    if (type === 'customer') {
      this.validateCustomerDetails = validationFn;
    }
  }

  handleDayMouseEnter(day, e) {
    const { availableDates, timeslotLoading } = this.state;
    const { apiVerbose, timeslots } = this.props;
    if (timeslotLoading) return;

    let tooltipParams = null;
    const duration = this.getDuration();
    const date = dayjs(day).format(SHORT_DATE_FORMAT);
    const tooltipEnabled = apiVerbose && (!!window.bta.isDemo || !!window.xprops?.isShopifyLogin);
    const nbr = this.getSpaceOnDay(date);

    if (nbr && availableDates.includes(date)) {
      this.setState({
        hoverStart: dayjs(date).toDate(),
        hoverEnteredTo: dayjs(date).add(duration - 1, 'seconds').toDate(),
      });
    } else if (timeslots && !document.querySelector('.card.help-info') && tooltipEnabled) {
      tooltipParams = getTooltipParams(find(timeslots, { date }), e.target);
      this.setState({ tooltipParams });
    }
  }

  handleSubmit(e) {
    const { order, orderActions: { submitAppointment }, bookingFields } = this.props;
    const messages = this.getValidMessage();
    const items = [{ external_id: order.variantSelected, quantity: order.quantity }];
    const isEnableBookingCreation = this.enabledBookingCreation();

    this.onSubmit({
      element: e,
      messages,
      itemsResponse: items,
      action: submitAppointment,
      bookingFields,
      isEnableBookingCreation,
    });
  }

  onBookingCreate(target) {
    const element = target;
    const { orderActions } = this.props;
    const t = new Translation(this.props);

    orderActions
      .createAppointment()
      .then(() => {
        this.closeModal();
        this.setState({ bookingCreated: true });
      })
      .catch((responsed) => {
        const defaultMessage = 'Something went wrong. Please contact our support team.';
        element.disabled = false;
        const { response } = responsed;

        if (response && response.data) {
          const messages = (Array.isArray(response.data.errors)) ? response.data.errors[0]?.messages : response.data;
          this.setState({ messages: [parseBookingResponseError(messages, t, defaultMessage)] });
        } else {
          this.setState({ messages: [defaultMessage] });
          throw responsed;
        }
      });
  }

  isSlotAvailable(timeslot) {
    if (!timeslot) {
      return false;
    }

    const { maxDate, minDate, timeZone, order: { totalQuantity } } = this.props;
    const now = dayjs().tz(timeZone);
    const minTime = now.add(minDate, 'seconds');
    const maxTime = maxDate ? now.clone().add(maxDate, 'days') : now.clone().add(2, 'years');
    const start = dayjs(timeslot.start).tz(timeZone);

    return timeslot.available >= totalQuantity && start.isSameOrAfter(minTime) && start.isSameOrBefore(maxTime);
  }

  isSlotsAvailable(timeslots) {
    const { order: { totalQuantity = 1 } } = this.props;
    return timeslots.every((timeslot) => {
      const overlapsTimeSlots = timeslots.filter((slot) => slot.start <= timeslot.finish && slot.finish >= timeslot.start);
      const sumQuantity = (overlapsTimeSlots?.length || 1) * totalQuantity;
      return timeslot.available >= sumQuantity && this.isSlotAvailable(timeslot);
    });
  }

  onDayClick(day) {
    const { availableDates, timeslotLoading } = this.state;
    const {
      multipleAppointmentsEnabled,
      timeslots,
      order: { timeSlots, multipleSlotsCount },
      orderActions: { setTimeSlot },
    } = this.props;

    if (timeslotLoading) return;
    // if the date is available
    const date = dayjs(day).format(SHORT_DATE_FORMAT);
    const nbr = this.showWaitlist() || this.getSpaceOnDay(date);

    if (nbr && availableDates.includes(date)) {
      let timeSlotSelected = null;

      if (timeslots.length) {
        const blockTimeSlot = find(timeslots, { date });
        if (blockTimeSlot) {
          const timeslotsBTS = blockTimeSlot.timeslots;

          timeSlotSelected = timeslotsBTS.filter((slot) => slot.status === 1 && (!timeSlots.length || timeSlots.every((x) => !isEqual(slot, x))))[0];
          if (!timeSlotSelected && !multipleAppointmentsEnabled) timeSlotSelected = timeslotsBTS[0];
        }
      }

      if (multipleSlotsCount === 1) {
        setTimeSlot({ timeSlot: timeSlotSelected });
        this.setState({ daySelected: day, timeSlotSelected });
      } else {
        this.setState({ daySelected: day, timeSlotSelected: null });
      }
    } else {
      setTimeSlot({ timeSlot: null });
      this.setState({ daySelected: null, timeSlotSelected: null });
    }
  }

  onMonthClick(day) {
    const {
      order: preOrder,
      exceededBookingsLimit,
      exceededTrialPlan,
      frameActions: { fetchBlocks },
      setting,
      timeZone,
      variants,
    } = this.props;

    const date = dayjs(day);
    const start = date.clone().startOf('month').startOf('week').subtract(1, 'days');
    const finish = date.clone().endOf('month').endOf('week').add(1, 'days');
    const isIntervalBased = setting.time_slot_generation === 'interval';
    const interval = setting.slot_interval ? setting.slot_interval : 60;
    const intervalValue = isIntervalBased ? interval : null;
    this.setState({ currentMonth: dayjs(day) });

    if (exceededTrialPlan || exceededBookingsLimit || isHiddenVariant(variants, preOrder.variantSelected)) {
      return null;
    }

    return fetchBlocks({
      externalId: preOrder.variantSelected,
      interval: intervalValue,
      finish,
      locationId: preOrder.selectedLocation ? preOrder.selectedLocation.id : [],
      resourceId: preOrder.selectedResource ? preOrder.selectedResource.id : [],
      start,
      timezone: timeZone,
    });
  }

  setDisabledDay(value) {
    const { availableDates } = this.state;
    const day = dayjs(value).format(SHORT_DATE_FORMAT);

    if (!availableDates.includes(day) || (!this.showWaitlist() && this.getSpaceOnDay(day) === 0)) {
      return new Date(day);
    }

    return null;
  }

  onSelectTimeSlot(start) {
    const self = this;
    const { daySelected } = this.state;
    const { timeslots, orderActions: { setTimeSlot } } = this.props;
    const date = dayjs(daySelected).format(SHORT_DATE_FORMAT);
    const blockTimeSlot = find(timeslots, { date });
    const timeSlot = find(blockTimeSlot.timeslots, { start });

    self.setState({ timeSlotSelected: timeSlot });
    setTimeSlot({ timeSlot });
  }

  renderDatePicker() {
    const { maxDate, minDate, order: { variantSelected }, region, shopBeginningOfWeek, style, timeZone } = this.props;
    const { currentMonth, daySelected, hoverEnteredTo, hoverStart, timeSlotSelected, tooltipParams } = this.state;
    const today = dayjs().tz(timeZone);
    const firstDate = minDate ? today.clone().add(minDate, 'seconds') : today.clone();
    const lastDate = maxDate ? today.clone().add(maxDate, 'days') : today.clone().add(2, 'years');
    const hoverHighlight = enumerateDaysBetweenDates(hoverStart, hoverEnteredTo);
    const additionalStyle = { inactive: { ...style.inactive } };
    const month = dayjs(currentMonth || firstDate).tz(timeZone).toDate();
    const duration = this.getDuration();
    const selectedTime = timeSlotSelected ? timeSlotSelected.start : null;
    const fromStr = dayjs(selectedTime).tz(timeZone).format(FULL_DATE_TIME_FORMAT);
    const from = selectedTime
      ? dayjs(fromStr).toDate()
      : dayjs(daySelected).startOf('day').add(1, 'minute').toDate();
    const to = dayjs(from).add(duration - 1, 'seconds').toDate();

    return (
      <>
        <DatePicker
          showOutsideDays
          shopBeginningOfWeek={shopBeginningOfWeek}
          styled={{ ...style.datepicker, ...additionalStyle }}
          region={region}
          fromMonth={variantSelected ? firstDate.toDate() : month}
          toMonth={variantSelected ? lastDate.toDate() : month}
          month={month}
          onMonthChange={this.onMonthClick}
          onDayClick={this.onDayClick}
          handleDayMouseEnter={this.handleDayMouseEnter}
          handleDayMouseLeave={this.handleDayMouseLeave}
          selectedDays={[from, { from, to }]}
          modifiers={{
            disabled: this.setDisabledDay,
            selected: daySelected,
            hoverStart,
            hoverEnteredTo,
            hoverHighlight,
            start: from,
            end: to,
          }}
        />
        <HelpTooltip
          messageStyles={tooltipParams?.messageStyles}
          onClose={this.handleCloseHelpTooltip}
          activateClassName="DayPicker-Day"
          show={!isEmpty(tooltipParams)}
          hideIcon
        >
          <TooltipDetails details={tooltipParams?.details} />
        </HelpTooltip>
      </>
    );
  }

  renderTimeSlotsSelect(value) {
    const {
      isFetchingBlocks,
      locale,
      maxDate,
      minDate,
      order: { quantity, timeSlots, variantSelected },
      setting,
      style,
      timeslots,
      timeZone,
      variants,
    } = this.props;

    const selectedVariant = variants.find((v) => v.external_id === variantSelected) || {};
    const { availableDates, timeSlotSelected } = this.state;
    const isDisplayRange = stringToBoolean(setting.display_time_range);
    const isShowRemainingSpace = stringToBoolean(setting.show_remaining_places);
    const isHideBookedOutSlots = setting.booked_out_timeslots === 'hide';

    const timeFormat = setting.time_format;

    return (
      <TimeSlotsSelect
        timeslots={timeslots}
        timeZone={timeZone}
        loading={isFetchingBlocks}
        locale={locale}
        isDisplayRange={isDisplayRange}
        isHideBookedOutSlots={isHideBookedOutSlots}
        isShowRemainingSpace={isShowRemainingSpace}
        isShowWaitlist={this.showWaitlist()}
        availableDates={availableDates}
        style={style}
        timeFormat={timeFormat}
        selectedTimeSlot={timeSlotSelected}
        selectedTimeSlots={timeSlots}
        selectedDate={dayjs(value).format(SHORT_DATE_FORMAT)}
        customClassName={this.getCustomClassName('mb5')}
        onSelect={(start) => this.onSelectTimeSlot(start)}
        onMenuOpen={() => { this.setTimeslotLoading(true); }}
        onMenuClose={() => { this.setTimeslotLoading(false); }}
        translation={new Translation(this.props)}
        quantity={quantity}
        maxDate={maxDate}
        minDate={minDate}
        variant={selectedVariant}
      />
    );
  }

  setStateOnGetCartItems({ element, messages, itemsResponse, action, isShowAlert }) {
    const { bookingToken } = this.props;
    const newElement = element;

    if (!messages.length) {
      newElement.target.disabled = true;
      const isEnabledBookingCreation = this.enabledBookingCreation();

      if (isEnabledBookingCreation && !bookingToken) {
        this.setState({ messages: [] }, () => { this.onBookingCreate(newElement.target); });
      } else {
        this.setState({ messages: [] }, () => this.onGetCartItems({
          element: newElement.target,
          itemsResponse,
          action,
          isShowAlert,
        }));
      }
    }
  }

  showConfirmationStep() {
    const {
      locations,
      order: { selectedLocation },
      setting: { show_location: showLocation },
    } = this.props;
    const showLocationMap = stringToBoolean(showLocation);
    const enabledBookingCreation = this.enabledBookingCreation();
    const enableLocationDropdown = this.getEnableLocationDropdown();
    const isEnabledBookingFields = this.isEnabledBookingFields();
    let location = selectedLocation;

    if (!location && locations.length === 1) {
      [location] = locations;
    }

    const address = location ? location.address : null;

    return (
      enabledBookingCreation
      || (showLocationMap && (enableLocationDropdown || address))
      || isEnabledBookingFields
    );
  }

  getCustomClassName(className) {
    if (!detectMob()) return '';

    const { daySelected } = this.state;
    const { setting } = this.props;
    return !daySelected || stringToBoolean(setting.show_timezone_chooser) ? '' : className;
  }

  render() {
    const { bookingCreated, daySelected, modalHeight, timeSlotSelected, bookingUpdated } = this.state;

    if (bookingUpdated) {
      return <UpdatedBookingPage />;
    }

    const {
      googleApiKey,
      isFetchingBlocks,
      locale,
      locations = [],
      order: {
        selectedLocation, selectedResource, timeSlots, variants, variantSelected,
      },
      orderActions,
      productTitle,
      setting,
      style,
      shopDatepickerTimeInterval,
      shopDateFormat,
      timeZone,
      brandingVisualized,
      variants: productVariants,
    } = this.props;
    const t = new Translation(this.props);
    const variantsValue = Object.values(variants);
    const { mode } = setting;
    const buttonMode = mode === 'button';
    const canBook = variantsValue.length && (timeSlots.length ? this.isSlotsAvailable(timeSlots) : this.isSlotAvailable(timeSlotSelected));
    const selectedTime = timeSlotSelected ? timeSlotSelected.start : null;
    const isRenderWaitlistButton = this.showWaitlist() && selectedTime && !canBook;
    const enableResourceDropdown = this.getEnableResourceDropdown();
    const enableLocationDropdown = this.getEnableLocationDropdown();
    const isVisibleVariant = !isHiddenVariant(productVariants, variantSelected);
    const showProductSelector = ['button', 'embed'].includes(mode);
    let subPanelClass = 'sub-panel';
    let subPanelStyle = { ...style.body, ...style.boxShadow };

    if (mode === 'popup' && detectIOS()) {
      subPanelStyle = { ...subPanelStyle, minHeight: '499px' };
    }

    if (!buttonMode) {
      subPanelClass += ` ${mode}`;
    }

    if (bookingCreated) {
      return (
        <CreatedBookingPage
          subPanelClass={subPanelClass}
          onNewBooking={() => window.top.location.reload()}
        />
      );
    }

    return (
      <div
        className={`center widget-container ${this.getCustomClassName('pb5')}`}
        style={{ minHeight: modalHeight + 20 }}>
        <StyledGlobalWrapper styles={style}>
          <CloseModalIcon onCloseFn={this.closeModal ? this.closeModal : () => {}} />
          <Alert ref={this.setAlertRef} />
          <div className={`${subPanelClass}`} style={subPanelStyle}>
            <h3 className="text-center widget-title" style={{ ...style.title }}>
              {t.key('title')}
            </h3>

            {this.renderExceededAlert()}
            {isVisibleVariant && this.renderLocationDropdown(isFetchingBlocks)}
            {isVisibleVariant && this.renderResourceDropdown(isFetchingBlocks, true)}

            {window.bta.showProductSelector && showProductSelector
              && (
                <ProductSelect
                  isDisabled={isFetchingBlocks}
                  className="mt10"
                  labelClass="titles"
                  wrapperClass="select-block"
                />
              )}
            {this.renderVariantSelect(isFetchingBlocks)}
            {this.renderQuantityInput(this.getMaxQuantity(), undefined, 0)}
            <PaymentOptions />

            {isVisibleVariant && (
              <div className="relative text-center">
                {this.renderDatePicker()}
                {daySelected && this.renderTimeSlotsSelect(daySelected)}
              </div>
            )}

            <MultipleAppointments locale={locale} isSlotAvailable={(timeslot) => this.isSlotAvailable(timeslot)} />
            <Price title={t.key('price')} customClassName={this.getCustomClassName('mb5')} />
            {this.renderWaitlist(selectedTime)}

            {isRenderWaitlistButton
              ? (
                this.renderWaitlistButton()
              ) : (
                this.renderButton(canBook, isFetchingBlocks, this.showConfirmationStep())
              )}
            <StyledTimeZone timeZone={timeZone} selectedTime={selectedTime} />

            {this.renderModal(
              <ConfirmationPage
                canBook={canBook}
                closeModal={this.closeModal ? this.closeModal : () => {}}
                dateFormat={shopDateFormat}
                enabledBookingCreation={this.enabledBookingCreation()}
                enabledBookingFields={this.isEnabledBookingFields()}
                enableLocationDropdown={enableLocationDropdown}
                enableResourceDropdown={enableResourceDropdown}
                getValidationFn={this.handleGetValidationFn}
                googleApiKey={googleApiKey}
                locale={locale}
                locations={locations}
                orderActions={orderActions}
                productTitle={productTitle}
                renderSubmitButton={this.renderSubmitButton}
                selectedLocation={selectedLocation}
                selectedTimeSlots={timeSlots}
                selectedResource={selectedResource}
                setting={setting}
                setWrapperHeight={this.setWrapperHeight}
                style={style}
                timeInterval={shopDatepickerTimeInterval}
                timeSlotSelected={timeSlotSelected}
                timeZone={timeZone}
                translation={t}
                variants={variantsValue}
              />,
              brandingVisualized
            )}
            {brandingVisualized && <BrandingString />}
          </div>
        </StyledGlobalWrapper>
      </div>
    );
  }
}

AppointmentPage.propTypes = {
  exceededBookingsLimit: PropTypes.bool,
  exceededTrialPlan: PropTypes.bool,
  locations: PropTypes.array,
  resources: PropTypes.array,
  order: PropTypes.object,
  orderActions: PropTypes.shape({
    setVariantsQuantity: PropTypes.func,
    setOrderDetails: PropTypes.func,
  }),
  cartActions: PropTypes.shape({
    addItems: PropTypes.func,
  }),
  style: PropTypes.object,
  setting: PropTypes.object,
  productTitle: PropTypes.string,
};
